/* ============================================================
   acg-cart-drawer.js
   Slide-in cart drawer for the ACG custom theme.

   Public API:
     window.ACGCartDrawer = {
       open():    Slide the drawer in, focus-trap, ESC-handler.
       close():   Slide it out, restore focus & scroll.
       refresh(): Pull /cart.js and re-render items + subtotal + badge.
     }

   Listens on document for the custom event `acg:cart:added` (dispatched
   by acg-ajax-cart.js after a successful add-to-cart) — calls refresh()
   then open(). Suppresses open() if the user is already on /cart.

   Accessibility — mirrors the pattern used by the acg-header mobile
   drawer (role="dialog", aria-modal, focus trap, ESC, focus restore).
   ============================================================ */
(function () {
  'use strict';

  if (window.ACGCartDrawer && window.ACGCartDrawer.initialised) {
    return;
  }

  /* ---- Money formatting ------------------------------------------- */
  // Conservative formatter — Shopify shop money_format is server-side
  // only, so we format with the shop's currency code and a USD fallback.
  // Inputs are integer cents (per Shopify Ajax cart API).
  function formatMoney(cents) {
    var n = (typeof cents === 'number') ? cents : parseInt(cents, 10);
    if (isNaN(n)) {
      return '';
    }
    var dollars = (n / 100).toFixed(2);
    // Add thousands separators
    var parts = dollars.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return '$' + parts.join('.');
  }

  /* ---- Element accessors ------------------------------------------ */
  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }

  var FOCUSABLE_SEL = [
    'a[href]', 'button:not([disabled])', 'input:not([disabled])',
    'select:not([disabled])', 'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  /* ---- Module state ----------------------------------------------- */
  var drawer = null;
  var panel = null;
  var overlay = null;
  var closeBtn = null;
  var body = null;
  var footer = null;
  var subtotalEl = null;
  var savingsRowEl = null;
  var savingsAmountEl = null;
  var countLabelEl = null;
  var lastFocusedEl = null;
  var keydownHandler = null;
  var openCloseDebounceTimer = null;
  var refreshInFlight = null;
  var onCartPage = false;

  function isOpen() {
    return !!(drawer && drawer.classList.contains('is-open'));
  }

  /* ---- Focus trap ------------------------------------------------- */
  function trapFocus(e) {
    if (e.key !== 'Tab') return;
    var focusables = $$(FOCUSABLE_SEL, panel).filter(function (el) {
      return el.offsetParent !== null || el === document.activeElement;
    });
    if (!focusables.length) {
      e.preventDefault();
      panel.focus();
      return;
    }
    var first = focusables[0];
    var last = focusables[focusables.length - 1];
    if (e.shiftKey) {
      if (document.activeElement === first || !panel.contains(document.activeElement)) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  /* ---- Open / close ----------------------------------------------- */
  function open() {
    // WSH / Wholesale Pricing Discount (WPD) only prices the full /cart page,
    // never this AJAX drawer — the app re-prices once on page load and exposes
    // no per-line re-trigger, so a re-rendered drawer always shows retail.
    // Send wholesale-tagged customers to /cart (correctly discounted) instead.
    // Retail customers are unaffected and keep the drawer.
    if (window.is_wcp_wholesale_customer === true && !onCartPage) {
      window.location.href = '/cart';
      return;
    }
    if (!drawer || isOpen()) return;
    lastFocusedEl = document.activeElement;
    drawer.classList.add('is-open');
    drawer.removeAttribute('aria-hidden');
    document.body.style.overflow = 'hidden';

    // Focus the close button after the transition starts
    setTimeout(function () {
      if (closeBtn) closeBtn.focus();
    }, 50);

    // ESC + focus trap handler
    keydownHandler = function (e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        close();
        return;
      }
      trapFocus(e);
    };
    document.addEventListener('keydown', keydownHandler);
  }

  function close() {
    if (!drawer || !isOpen()) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';

    if (keydownHandler) {
      document.removeEventListener('keydown', keydownHandler);
      keydownHandler = null;
    }

    // Restore focus
    if (lastFocusedEl && typeof lastFocusedEl.focus === 'function') {
      try { lastFocusedEl.focus(); } catch (_) { /* noop */ }
    }
    lastFocusedEl = null;
  }

  /* ---- Debounce wrapper for rapid clicks -------------------------- */
  function debouncedOpen() {
    if (openCloseDebounceTimer) clearTimeout(openCloseDebounceTimer);
    openCloseDebounceTimer = setTimeout(function () {
      open();
    }, 60);
  }

  /* ---- Render helpers --------------------------------------------- */
  function getCartAdd(routeKey, fallback) {
    if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
      return window.Shopify.routes.root + routeKey;
    }
    return fallback;
  }

  function updateCountBadge(count) {
    var badges = $$('.acg-nav__cart-count');
    badges.forEach(function (b) {
      b.textContent = count;
      if (count > 0) b.removeAttribute('hidden');
      else b.setAttribute('hidden', '');
    });
    var links = $$('.acg-nav__cart');
    links.forEach(function (l) {
      var noun = count === 1 ? 'item' : 'items';
      l.setAttribute('aria-label', 'Cart — ' + count + ' ' + noun);
    });
    if (countLabelEl) {
      countLabelEl.textContent = '(' + count + ')';
    }
    // Close control shows "Continue Shopping →" when there are items, else the × icon.
    if (closeBtn) {
      if (count > 0) {
        closeBtn.classList.add('is-continue');
        closeBtn.setAttribute('aria-label', 'Continue shopping');
      } else {
        closeBtn.classList.remove('is-continue');
        closeBtn.setAttribute('aria-label', 'Close cart');
      }
    }
  }

  function escapeHTML(str) {
    if (str == null) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /* ---- Free-gift lines --------------------------------------------- */
  // acg-free-gift-auto.js marks the lines it adds with a hidden property.
  // Those get a plain "Free gift" badge — the raw discount titles are
  // internal codes (FOAMCANNONQUANPINK) and not customer-facing language.
  // Only badge a line that genuinely landed at $0, so a promo that failed
  // to apply is never labelled free.
  function isFreeGiftLine(item) {
    var cfg = window.ACGFreeGiftAutoConfig;
    if (!cfg || !item || !item.properties) {
      return false;
    }
    if (!item.properties[cfg.markerKey || '_free_gift']) {
      return false;
    }
    return item.final_line_price === 0;
  }

  function renderItemRow(item, index) {
    var lineIndex = index + 1;
    var imageSrc = '';
    if (item.featured_image && item.featured_image.url) {
      // Shopify image_url-style: append width param
      imageSrc = item.featured_image.url;
      // Try to request 160w if URL is on cdn.shopify.com
      if (/\bcdn\.shop(ify)?\b/.test(imageSrc) && imageSrc.indexOf('?') === -1) {
        imageSrc += '?width=160';
      }
    } else if (item.image) {
      imageSrc = item.image;
    }
    var variantLine = '';
    var hideVariant =
      !item.variant_title ||
      item.variant_title === 'Default Title' ||
      item.product_has_only_default_variant === true;
    if (!hideVariant && item.variant_title) {
      variantLine =
        '<p class="acg-cart-drawer__item-variant">' +
        escapeHTML(item.variant_title) + '</p>';
    }
    var imageMarkup = '';
    if (imageSrc) {
      imageMarkup =
        '<img src="' + escapeHTML(imageSrc) + '" alt="' +
        escapeHTML(item.product_title || item.title || '') +
        '" loading="lazy" width="160" height="160">';
    }

    // Line-level discount (bundle / BxGy / automatic): show the discount name
    // and the new price with the original struck through.
    var hasLineDiscount =
      item.original_line_price != null &&
      item.final_line_price != null &&
      item.final_line_price < item.original_line_price;

    var priceMarkup;
    if (hasLineDiscount) {
      priceMarkup =
        '<span class="acg-cart-drawer__item-price acg-cart-drawer__item-price--sale" data-acg-cart-line-price>' +
          '<s class="acg-cart-drawer__item-was">' + formatMoney(item.original_line_price) + '</s>' +
          '<span class="acg-cart-drawer__item-now">' + formatMoney(item.final_line_price) + '</span>' +
        '</span>';
    } else {
      priceMarkup =
        '<span class="acg-cart-drawer__item-price" data-acg-cart-line-price>' +
          formatMoney(item.final_line_price != null ? item.final_line_price : item.line_price) +
        '</span>';
    }

    var TAG_ICON =
      '<svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20.59 13.41 13.42 20.6a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><circle cx="7" cy="7" r="1.2"/></svg>';

    var discountTags = '';
    if (isFreeGiftLine(item)) {
      discountTags =
        '<span class="acg-cart-drawer__item-discount acg-cart-drawer__item-discount--gift">' +
          TAG_ICON + 'Free gift' +
        '</span>';
    } else if (item.discounts && item.discounts.length) {
      for (var di = 0; di < item.discounts.length; di++) {
        var d = item.discounts[di];
        if (d && d.amount > 0) {
          discountTags +=
            '<span class="acg-cart-drawer__item-discount">' +
              TAG_ICON +
              escapeHTML(d.title) + ' (−' + formatMoney(d.amount) + ')' +
            '</span>';
        }
      }
    }

    return (
      '<li class="acg-cart-drawer__item" data-line-key="' +
      escapeHTML(item.key) + '" data-line-index="' + lineIndex + '">' +
        '<a href="' + escapeHTML(item.url || '#') + '" class="acg-cart-drawer__item-image" tabindex="-1" aria-hidden="true">' +
          imageMarkup +
        '</a>' +
        '<div class="acg-cart-drawer__item-info">' +
          '<a href="' + escapeHTML(item.url || '#') + '" class="acg-cart-drawer__item-title">' +
            escapeHTML(item.product_title || item.title || '') +
          '</a>' +
          variantLine +
          discountTags +
          '<div class="acg-cart-drawer__item-row">' +
            '<div class="acg-cart-drawer__qty" role="group" aria-label="Quantity">' +
              '<button type="button" class="acg-cart-drawer__qty-btn" data-acg-cart-qty-decrease data-line-index="' + lineIndex + '" aria-label="Decrease quantity">−</button>' +
              '<input type="number" class="acg-cart-drawer__qty-input" value="' +
                escapeHTML(item.quantity) +
                '" min="0" step="1" inputmode="numeric" data-acg-cart-qty-input data-line-index="' + lineIndex + '" aria-label="Quantity">' +
              '<button type="button" class="acg-cart-drawer__qty-btn" data-acg-cart-qty-increase data-line-index="' + lineIndex + '" aria-label="Increase quantity">+</button>' +
            '</div>' +
            priceMarkup +
          '</div>' +
          '<button type="button" class="acg-cart-drawer__remove" data-acg-cart-remove data-line-index="' + lineIndex + '" aria-label="Remove ' +
            escapeHTML(item.product_title || item.title || '') +
            ' from cart">Remove</button>' +
        '</div>' +
      '</li>'
    );
  }

  function renderEmpty() {
    return (
      '<div class="acg-cart-drawer__empty" data-acg-cart-empty>' +
        '<svg class="acg-cart-drawer__empty-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
          '<path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/>' +
          '<line x1="3" y1="6" x2="21" y2="6"/>' +
          '<path d="M16 10a4 4 0 0 1-8 0"/>' +
        '</svg>' +
        '<p class="acg-cart-drawer__empty-msg">Your cart is empty</p>' +
        '<a href="/" class="acg-cart-drawer__empty-link" data-acg-cart-close-link>Continue shopping</a>' +
      '</div>'
    );
  }

  function updateSavings(cart) {
    if (!savingsRowEl || !savingsAmountEl) return;
    var d = (cart && cart.total_discount) ? cart.total_discount : 0;
    if (d > 0) {
      savingsAmountEl.textContent = '−' + formatMoney(d);
      savingsRowEl.removeAttribute('hidden');
    } else {
      savingsRowEl.setAttribute('hidden', '');
    }
  }

  function renderCart(cart) {
    if (!body) return;
    if (!cart || cart.item_count === 0) {
      body.innerHTML = renderEmpty();
      if (footer) footer.setAttribute('hidden', '');
    } else {
      var html = '<ul class="acg-cart-drawer__items" data-acg-cart-items role="list">';
      for (var i = 0; i < cart.items.length; i++) {
        html += renderItemRow(cart.items[i], i);
      }
      html += '</ul>';
      body.innerHTML = html;
      if (footer) footer.removeAttribute('hidden');
      if (subtotalEl) {
        subtotalEl.textContent = formatMoney(cart.total_price);
      }
      updateSavings(cart);
    }
    updateCountBadge(cart ? cart.item_count : 0);
  }

  /* ---- /cart.js refresh ------------------------------------------- */
  function refresh() {
    if (refreshInFlight) return refreshInFlight;
    var url = getCartAdd('cart.js', '/cart.js');
    refreshInFlight = fetch(url, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      cache: 'no-store'
    })
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        renderCart(cart);
        refreshInFlight = null;
        return cart;
      })
      .catch(function (err) {
        refreshInFlight = null;
        // Don't break the drawer on a network blip
        if (window.console && window.console.warn) {
          console.warn('[ACGCartDrawer] refresh failed', err);
        }
      });
    return refreshInFlight;
  }

  /* ---- Line update helpers ---------------------------------------- */
  function setLineUpdating(rowEl, isUpdating) {
    if (!rowEl) return;
    if (isUpdating) rowEl.classList.add('is-updating');
    else rowEl.classList.remove('is-updating');
  }

  function changeLine(line, quantity, rowEl) {
    setLineUpdating(rowEl, true);
    var url = getCartAdd('cart/change.js', '/cart/change.js');
    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      credentials: 'same-origin',
      body: JSON.stringify({ line: line, quantity: quantity })
    })
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        renderCart(cart);
      })
      .catch(function () {
        setLineUpdating(rowEl, false);
      });
  }

  /* ---- Click delegation ------------------------------------------- */
  function bindEvents() {
    if (overlay) {
      overlay.addEventListener('click', function () { close(); });
    }
    if (closeBtn) {
      closeBtn.addEventListener('click', function () { close(); });
    }
    if (panel) {
      // Delegate clicks for qty +/-, remove, and continue-shopping link
      panel.addEventListener('click', function (e) {
        var t = e.target;
        if (!t) return;
        var closeLink = t.closest && t.closest('[data-acg-cart-close-link]');
        if (closeLink) {
          // Let the navigation happen; close the drawer first
          close();
          return;
        }
        var dec = t.closest && t.closest('[data-acg-cart-qty-decrease]');
        if (dec) {
          var row = dec.closest('.acg-cart-drawer__item');
          var idx = parseInt(dec.getAttribute('data-line-index'), 10);
          var input = row && row.querySelector('[data-acg-cart-qty-input]');
          var current = input ? parseInt(input.value, 10) : 1;
          if (isNaN(current)) current = 1;
          var next = Math.max(0, current - 1);
          changeLine(idx, next, row);
          return;
        }
        var inc = t.closest && t.closest('[data-acg-cart-qty-increase]');
        if (inc) {
          var row2 = inc.closest('.acg-cart-drawer__item');
          var idx2 = parseInt(inc.getAttribute('data-line-index'), 10);
          var input2 = row2 && row2.querySelector('[data-acg-cart-qty-input]');
          var current2 = input2 ? parseInt(input2.value, 10) : 1;
          if (isNaN(current2)) current2 = 1;
          changeLine(idx2, current2 + 1, row2);
          return;
        }
        var rm = t.closest && t.closest('[data-acg-cart-remove]');
        if (rm) {
          var row3 = rm.closest('.acg-cart-drawer__item');
          var idx3 = parseInt(rm.getAttribute('data-line-index'), 10);
          changeLine(idx3, 0, row3);
          return;
        }
      });

      // Manual quantity input edit — commit on change / Enter
      panel.addEventListener('change', function (e) {
        var t = e.target;
        if (!t || !t.matches || !t.matches('[data-acg-cart-qty-input]')) return;
        var row = t.closest('.acg-cart-drawer__item');
        var idx = parseInt(t.getAttribute('data-line-index'), 10);
        var val = parseInt(t.value, 10);
        if (isNaN(val) || val < 0) val = 0;
        changeLine(idx, val, row);
      });
    }
  }

  /* ---- Listen for add-to-cart events ------------------------------ */
  function bindCartAddedListener() {
    document.addEventListener('acg:cart:added', function () {
      // If we're already on the cart page, don't open the drawer over
      // the same data — just refresh the count.
      var alreadyOnCart =
        onCartPage ||
        (typeof window !== 'undefined' &&
         window.location &&
         window.location.pathname === '/cart');
      if (alreadyOnCart) {
        refresh();
        return;
      }
      refresh().then(function () {
        debouncedOpen();
      });
    });
  }

  /* ---- Init ------------------------------------------------------- */
  function init() {
    drawer = document.getElementById('acg-cart-drawer');
    if (!drawer) return;
    panel = drawer.querySelector('.acg-cart-drawer__panel');
    overlay = drawer.querySelector('[data-acg-cart-overlay]');
    closeBtn = drawer.querySelector('[data-acg-cart-close]');
    body = drawer.querySelector('[data-acg-cart-body]');
    footer = drawer.querySelector('[data-acg-cart-footer]');
    subtotalEl = drawer.querySelector('[data-acg-cart-subtotal]');
    savingsRowEl = drawer.querySelector('[data-acg-cart-savings-row]');
    savingsAmountEl = drawer.querySelector('[data-acg-cart-savings]');
    countLabelEl = drawer.querySelector('[data-acg-cart-count-label]');

    onCartPage = drawer.getAttribute('data-on-cart-page') === 'true';

    bindEvents();
    bindCartAddedListener();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* ---- Public API ------------------------------------------------- */
  window.ACGCartDrawer = {
    initialised: true,
    open: function () {
      if (!drawer) return;
      if (onCartPage) {
        // On the cart page itself, opening the drawer would just
        // duplicate the page UI. Refresh the count instead.
        refresh();
        return;
      }
      debouncedOpen();
    },
    close: close,
    refresh: refresh
  };
}());
